import { create } from 'zustand';
import {
  WasteEntry,
  CreateWasteRequest,
  UpdateWasteRequest,
  WasteLinkUpdate,
  WasteLinkResolution,
  WasteLinkCommitSummary,
  fetchWaste,
  createWaste,
  updateWaste,
  deleteWaste,
  updateWasteLink,
  commitWasteLinks,
} from '../services/waste/wasteService';

interface WasteState {
  wasteList: WasteEntry[];
  isLoading: boolean;
  currentRestaurantId: string | null;
  load: (restaurantId: string) => Promise<void>;
  create: (restaurantId: string, data: CreateWasteRequest) => Promise<void>;
  update: (id: string, data: UpdateWasteRequest) => Promise<void>;
  delete: (id: string) => Promise<void>;
  /** Link / relink / unlink one entry by hand, then refetch. */
  updateLink: (id: string, update: WasteLinkUpdate) => Promise<void>;
  /** Apply a reviewed bulk link plan, then refetch. */
  commitLinks: (restaurantId: string, resolutions: WasteLinkResolution[]) => Promise<WasteLinkCommitSummary>;
  reset: () => void;
}

export const useWasteStore = create<WasteState>((set, get) => ({
  wasteList: [],
  isLoading: false,
  currentRestaurantId: null,

  load: async (restaurantId: string) => {
    set({ isLoading: true });
    try {
      const data = await fetchWaste(restaurantId);
      set({ wasteList: data, currentRestaurantId: restaurantId, isLoading: false });
    } catch (err) {
      console.error('[ERROR] loading waste entries', err);
      set({ isLoading: false });
    }
  },

  create: async (restaurantId: string, data: CreateWasteRequest) => {
    try {
      await createWaste(restaurantId, data);
      await get().load(restaurantId);
    } catch (err) {
      console.error('[ERROR] creating waste entry', err);
      throw err;
    }
  },

  update: async (id: string, data: UpdateWasteRequest) => {
    try {
      await updateWaste(id, data);
      const restaurantId = get().currentRestaurantId;
      if (restaurantId) await get().load(restaurantId);
    } catch (err) {
      console.error('[ERROR] updating waste entry', err);
      throw err;
    }
  },

  delete: async (id: string) => {
    try {
      await deleteWaste(id);
      const restaurantId = get().currentRestaurantId;
      if (restaurantId) await get().load(restaurantId);
    } catch (err) {
      console.error('[ERROR] deleting waste entry', err);
      throw err;
    }
  },

  updateLink: async (id: string, update: WasteLinkUpdate) => {
    try {
      await updateWasteLink(id, update);
      const restaurantId = get().currentRestaurantId;
      if (restaurantId) await get().load(restaurantId);
    } catch (err) {
      console.error('[ERROR] updating waste link', err);
      throw err;
    }
  },

  commitLinks: async (restaurantId: string, resolutions: WasteLinkResolution[]) => {
    try {
      const summary = await commitWasteLinks(restaurantId, resolutions);
      await get().load(restaurantId);
      return summary;
    } catch (err) {
      console.error('[ERROR] committing waste links', err);
      throw err;
    }
  },

  reset: () => {
    set({ wasteList: [], isLoading: false, currentRestaurantId: null });
  },
}));
